import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import {
  CreditCard,
  RectangleHorizontal,
  RectangleVertical,
  RotateCw,
  ExternalLink,
  Palette,
  ChevronLeft,
  ChevronDown,
  Plus,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  BusinessCard,
  FlipHint,
  type AvatarShapeMode,
  type WebsiteIconMode,
} from '@/components/BusinessCard';
import { CardDownload } from '@/components/CardDownload';
import {
  cardSize,
  DEFAULT_FIELD_VISIBILITY,
  type Orientation,
  type CardFieldVisibility,
} from '@/lib/cardFormat';
import { QRCodeSVG } from '@/components/ui/qrcode';
import { useCardData } from '@/hooks/useCardData';
import { useProfileBadges } from '@/hooks/useProfileBadges';
import { getAvatarShape } from '@/lib/avatarShape';
import { genUserName } from '@/lib/genUserName';
import { qrColors } from '@/lib/profileTheme';
import { DEFAULT_QR_STYLE, QR_STYLES, type QrStyle } from '@/lib/qrStyles';
import {
  DEFAULT_QR_TARGET,
  displayUrl,
  extraProfileLinks,
  resolveCustomLinks,
  resolveQrUrl,
  type CustomLink,
  type QrTarget,
  type QrTargetKind,
} from '@/lib/cardLinks';
import { cn } from '@/lib/utils';

interface CardViewerProps {
  pubkey: string;
  npub: string;
}

export function CardViewer({ pubkey, npub }: CardViewerProps) {
  const { metadata, theme, isLoading, hasTheme, themeTitle } = useCardData(pubkey);
  const { badges } = useProfileBadges(pubkey);

  const [orientation, setOrientation] = useState<Orientation>('horizontal');
  const [flipped, setFlipped] = useState(false);
  const [headline, setHeadline] = useState('');
  const [nameOverride, setNameOverride] = useState('');
  const [extraText, setExtraText] = useState('');
  const [qrStyle, setQrStyle] = useState<QrStyle>(DEFAULT_QR_STYLE);
  const [qrTarget, setQrTarget] = useState<QrTarget>(DEFAULT_QR_TARGET);
  const [fields, setFields] = useState<CardFieldVisibility>(DEFAULT_FIELD_VISIBILITY);
  const [avatarShape, setAvatarShape] = useState<AvatarShapeMode>('shape');
  const [websiteIcon, setWebsiteIcon] = useState<WebsiteIconMode>('glyph');
  const [showQrUrl, setShowQrUrl] = useState(true);
  const [qrLogo, setQrLogo] = useState(false);
  // Extra kind-0 links are opt-in per field id; hand-typed rows are always shown.
  const [enabledExtras, setEnabledExtras] = useState<Record<string, boolean>>({});
  const [customRows, setCustomRows] = useState<CustomLink[]>([]);

  const toggleField = (key: keyof CardFieldVisibility) =>
    setFields((f) => ({ ...f, [key]: !f[key] }));

  // Additional URL-valued fields the profile carries beyond the standard ones.
  const discoveredLinks = useMemo(() => extraProfileLinks(metadata), [metadata]);
  const extraLinks = useMemo(
    () => [
      ...discoveredLinks.filter((l) => enabledExtras[l.id]),
      ...resolveCustomLinks(customRows),
    ],
    [discoveredLinks, enabledExtras, customRows],
  );

  const addCustomRow = () =>
    setCustomRows((rows) => [...rows, { id: `custom:${Date.now()}`, url: '' }]);
  const updateCustomRow = (id: string, url: string) =>
    setCustomRows((rows) => rows.map((r) => (r.id === id ? { ...r, url } : r)));
  const removeCustomRow = (id: string) =>
    setCustomRows((rows) => rows.filter((r) => r.id !== id));

  // Which fields actually exist on the profile (only offer toggles for these).
  const availableFields: { key: keyof CardFieldVisibility; label: string; present: boolean }[] = [
    { key: 'avatar', label: 'Avatar', present: !!metadata?.picture },
    { key: 'bio', label: 'Bio / headline', present: !!(headline.trim() || metadata?.about) },
    { key: 'nip05', label: 'NIP-05', present: !!metadata?.nip05 },
    { key: 'website', label: 'Website', present: !!metadata?.website },
    { key: 'lud16', label: 'Lightning address', present: !!metadata?.lud16 },
    { key: 'badges', label: 'Badges', present: badges.length > 0 },
  ];

  // The shape choice is only meaningful when the profile actually carries one.
  const profileShape = getAvatarShape(metadata);

  const resolvedName = nameOverride.trim()
    || metadata?.display_name
    || metadata?.name
    || genUserName(pubkey);

  // Where the QR actually points, after falling back for targets the profile
  // can't satisfy (no website set, blank or non-https custom URL).
  const qrUrl = resolveQrUrl(qrTarget, {
    website: metadata?.website,
    nip05: metadata?.nip05,
    npub,
  });

  // QR colors are derived from the theme, so the style swatches preview the
  // exact colors the card will use — only the module shape differs.
  const qr = qrColors(theme);

  useSeoMeta({
    title: `${resolvedName} — Nostr Business Card | CardFlip`,
    description: metadata?.about
      ? metadata.about.slice(0, 150)
      : `${resolvedName}'s Nostr business card on CardFlip.`,
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 to-slate-900 text-slate-100">
      <header className="flex items-center justify-between px-4 py-5 sm:px-10">
        <Link
          to="/"
          className="flex items-center gap-2 font-bold tracking-tight text-slate-100 transition-opacity hover:opacity-80"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-lg">
            <CreditCard className="h-5 w-5 text-white" />
          </span>
          <span className="text-lg">CardFlip</span>
        </Link>
        <Button asChild variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-white/10">
          <Link to="/">
            <ChevronLeft className="mr-1 h-4 w-4" />
            New card
          </Link>
        </Button>
      </header>

      <main className="mx-auto grid w-full max-w-5xl gap-10 px-4 py-8 sm:px-10 lg:grid-cols-[minmax(0,1fr)_320px]">
        {/* Card stage */}
        <section className="flex min-w-0 flex-col items-center justify-start gap-6 lg:pt-8">
          {isLoading ? (
            <CardSkeleton orientation={orientation} />
          ) : (
            <>
              <BusinessCard
                npub={npub}
                metadata={metadata}
                theme={theme}
                orientation={orientation}
                flipped={flipped}
                onFlip={() => setFlipped((f) => !f)}
                headline={headline}
                nameOverride={nameOverride}
                extraText={extraText}
                fields={fields}
                avatarShape={avatarShape}
                websiteIcon={websiteIcon}
                badges={badges}
                extraLinks={extraLinks}
                qrStyle={qrStyle}
                qrTarget={qrTarget}
                showQrUrl={showQrUrl}
                qrLogo={qrLogo}
              />
              <FlipHint onFlip={() => setFlipped((f) => !f)} />
            </>
          )}

          <ThemeBadge hasTheme={hasTheme} themeTitle={themeTitle} isLoading={isLoading} />
        </section>

        {/* Controls. Each group collapses so the panel reads as a short menu
            rather than one long scroll of every option at once. */}
        <aside className="min-w-0 space-y-3">
          <Section title="Shape & text" defaultOpen>
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Orientation</Label>
              <ToggleGroup
                type="single"
                value={orientation}
                onValueChange={(v) => v && setOrientation(v as Orientation)}
                className="grid grid-cols-2 gap-2"
              >
                <OrientationToggle value="horizontal" active={orientation === 'horizontal'}>
                  <RectangleHorizontal className="h-4 w-4" />
                  Horizontal
                </OrientationToggle>
                <OrientationToggle value="vertical" active={orientation === 'vertical'}>
                  <RectangleVertical className="h-4 w-4" />
                  Vertical
                </OrientationToggle>
              </ToggleGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name-override" className="text-xs text-slate-400">
                Name <span className="text-slate-600">(optional override)</span>
              </Label>
              <Input
                id="name-override"
                value={nameOverride}
                onChange={(e) => setNameOverride(e.target.value)}
                placeholder={metadata?.display_name || metadata?.name || 'Display name'}
                className="border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-violet-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="headline" className="text-xs text-slate-400">
                Headline <span className="text-slate-600">(optional)</span>
              </Label>
              <Input
                id="headline"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder={metadata?.about ? 'Override the bio…' : 'Add a headline…'}
                className="border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-violet-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="extra-text" className="text-xs text-slate-400">
                Extra text <span className="text-slate-600">(optional)</span>
              </Label>
              <Textarea
                id="extra-text"
                value={extraText}
                onChange={(e) => setExtraText(e.target.value)}
                rows={3}
                placeholder="Title, tagline, services, contact hours…"
                className="resize-none border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-violet-500"
              />
              <p className="text-[11px] text-slate-600">
                Shows best on the vertical card, which has more room.
              </p>
            </div>
          </Section>

          <Section
            title="What's on the card"
            summary={`${availableFields.filter((f) => f.present && fields[f.key]).length} shown`}
          >
            {/* Each row carries its own options on the right, so a field and
                the way it's drawn stay in one place instead of the options
                collecting in a separate block further down the panel. */}
            <div className="space-y-2.5">
              {availableFields.map((f) => (
                <div key={f.key} className="flex min-h-8 items-center gap-2.5 text-sm">
                  <label
                    className={cn(
                      'flex min-w-0 flex-1 items-center gap-2.5',
                      f.present
                        ? 'text-slate-200 cursor-pointer'
                        : 'text-slate-600 cursor-not-allowed',
                    )}
                  >
                    <Checkbox
                      checked={fields[f.key] && f.present}
                      disabled={!f.present}
                      onCheckedChange={() => toggleField(f.key)}
                      className="border-white/25 data-[state=checked]:border-violet-500 data-[state=checked]:bg-violet-500"
                    />
                    <span className="truncate">{f.label}</span>
                    {!f.present && <span className="text-[10px] text-slate-700">(not set)</span>}
                  </label>

                  {/* Ditto profiles can mask the avatar to an emoji. A card is
                      a more formal object, so the owner gets to opt back into
                      a plain circle. */}
                  {f.key === 'avatar' && profileShape && fields.avatar && (
                    <InlineChoice
                      label="Avatar shape"
                      value={avatarShape}
                      onChange={setAvatarShape}
                      options={[
                        { id: 'shape', label: profileShape, title: `Profile shape ${profileShape}` },
                        { id: 'circle', label: 'Circle', title: 'Plain circle' },
                      ]}
                    />
                  )}

                  {/* The generic glyph keeps the contact strip in one accent
                      color; the site's own favicon is more recognizable but
                      brings its own palette, so it's the owner's call. */}
                  {f.key === 'website' && metadata?.website && fields.website && (
                    <InlineChoice
                      label="Website icon"
                      value={websiteIcon}
                      onChange={setWebsiteIcon}
                      options={[
                        { id: 'glyph', label: 'Globe', title: 'Generic globe glyph' },
                        { id: 'favicon', label: 'Favicon', title: "The site's own favicon" },
                      ]}
                    />
                  )}
                </div>
              ))}
            </div>
          </Section>

          <Section
            title="Extra links"
            summary={extraLinks.length ? `${extraLinks.length} added` : undefined}
          >
            {discoveredLinks.length > 0 && (
              <div className="space-y-2.5">
                {discoveredLinks.map((l) => (
                  <label
                    key={l.id}
                    className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-200"
                  >
                    <Checkbox
                      checked={!!enabledExtras[l.id]}
                      onCheckedChange={() =>
                        setEnabledExtras((e) => ({ ...e, [l.id]: !e[l.id] }))
                      }
                      className="border-white/25 data-[state=checked]:border-violet-500 data-[state=checked]:bg-violet-500"
                    />
                    <span className="min-w-0 truncate">
                      {l.label}
                      <span className="text-slate-500"> — {l.text}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}

            {customRows.map((row) => (
              <div key={row.id} className="flex items-center gap-2">
                <Input
                  value={row.url}
                  onChange={(e) => updateCustomRow(row.id, e.target.value)}
                  placeholder="https://…"
                  aria-label="Extra link URL"
                  className="border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-violet-500"
                />
                <button
                  type="button"
                  onClick={() => removeCustomRow(row.id)}
                  aria-label="Remove link"
                  className="rounded-lg border border-white/15 p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}

            <Button
              variant="outline"
              size="sm"
              onClick={addCustomRow}
              className="w-full border-white/15 bg-transparent text-slate-300 hover:bg-white/10 hover:text-white"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add a link
            </Button>
            <p className="text-[11px] text-slate-600">
              Only https links are printed on the card.
            </p>
          </Section>

          <Section title="QR code" summary={displayUrl(qrUrl)}>
            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Points to</Label>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { id: 'website', label: 'Website', enabled: !!metadata?.website },
                  { id: 'profile', label: 'Profile', enabled: true },
                  { id: 'custom', label: 'Custom', enabled: true },
                ] as { id: QrTargetKind; label: string; enabled: boolean }[]).map((t) => (
                  <PillButton
                    key={t.id}
                    active={qrTarget.kind === t.id}
                    disabled={!t.enabled}
                    onClick={() => setQrTarget((q) => ({ ...q, kind: t.id }))}
                  >
                    {t.label}
                  </PillButton>
                ))}
              </div>
              {qrTarget.kind === 'custom' && (
                <Input
                  value={qrTarget.custom}
                  onChange={(e) => setQrTarget((q) => ({ ...q, custom: e.target.value }))}
                  placeholder="https://…"
                  aria-label="Custom QR URL"
                  className="border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-violet-500"
                />
              )}
              <a
                href={qrUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 break-all text-xs text-violet-300 transition-colors hover:text-violet-200"
              >
                <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                Scans to {displayUrl(qrUrl)}
              </a>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-slate-400">Style</Label>
              <div className="grid grid-cols-3 gap-2">
                {QR_STYLES.map((s) => {
                  const active = qrStyle === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setQrStyle(s.id)}
                      aria-pressed={active}
                      className={cn(
                        'flex flex-col items-center gap-1.5 rounded-lg border px-2 py-2.5 text-xs transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500',
                        active
                          ? 'border-violet-500 bg-violet-500/20 text-white'
                          : 'border-white/15 bg-transparent text-slate-300 hover:bg-white/5',
                      )}
                    >
                      <span
                        className="overflow-hidden rounded"
                        style={{ backgroundColor: qr.bg }}
                      >
                        <QRCodeSVG
                          value={qrUrl}
                          size={44}
                          style={s.id}
                          fgColor={qr.fg}
                          bgColor={qr.bg}
                        />
                      </span>
                      {s.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-slate-600">
                Colors come from the profile's Ditto theme.
              </p>
            </div>

            <div className="space-y-2.5 border-t border-white/10 pt-4">
              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-200">
                <Checkbox
                  checked={showQrUrl}
                  onCheckedChange={() => setShowQrUrl((v) => !v)}
                  className="border-white/25 data-[state=checked]:border-violet-500 data-[state=checked]:bg-violet-500"
                />
                <span>Print the address below the code</span>
              </label>
              <label className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-200">
                <Checkbox
                  checked={qrLogo}
                  onCheckedChange={() => setQrLogo((v) => !v)}
                  className="border-white/25 data-[state=checked]:border-violet-500 data-[state=checked]:bg-violet-500"
                />
                <span>Favicon in the center</span>
              </label>
              <p className="text-[11px] text-slate-600">
                A center favicon switches the code to the highest error correction, so it still
                scans.
              </p>
            </div>
          </Section>

          {/* Only offered once the profile has loaded: exporting a skeleton
              would just save an empty card. */}
          {!isLoading && (
            <Section title="Download" summary="Front and back, as PNG">
              <CardDownload
                name={resolvedName}
                npub={npub}
                metadata={metadata}
                theme={theme}
                orientation={orientation}
                headline={headline}
                nameOverride={nameOverride}
                extraText={extraText}
                fields={fields}
                avatarShape={avatarShape}
                websiteIcon={websiteIcon}
                badges={badges}
                extraLinks={extraLinks}
                qrStyle={qrStyle}
                qrTarget={qrTarget}
                showQrUrl={showQrUrl}
                qrLogo={qrLogo}
              />
            </Section>
          )}

          <Button
            variant="outline"
            onClick={() => setFlipped((f) => !f)}
            className="w-full border-white/15 bg-transparent text-slate-200 hover:bg-white/10 hover:text-white"
          >
            <RotateCw className="mr-2 h-4 w-4" />
            Flip card
          </Button>
        </aside>
      </main>

      <footer className="px-4 py-6 text-center text-xs text-slate-500 sm:px-10">
        <a
          href="https://shakespeare.diy"
          target="_blank"
          rel="noopener noreferrer"
          className="transition-colors hover:text-slate-300"
        >
          Vibed with Shakespeare
        </a>
      </footer>
    </div>
  );
}

/**
 * One collapsible group of options. The header doubles as a status line — the
 * `summary` reports the group's current state so the panel can stay closed
 * without hiding what it's set to.
 */
function Section({
  title,
  summary,
  defaultOpen,
  children,
}: {
  title: string;
  summary?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(!!defaultOpen);

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm"
    >
      <CollapsibleTrigger className="flex w-full items-center gap-3 rounded-2xl px-5 py-4 text-left transition-colors hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500">
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-slate-200">{title}</span>
          {summary && !open && (
            <span className="mt-0.5 block truncate text-xs text-slate-500">{summary}</span>
          )}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-slate-400 transition-transform motion-reduce:transition-none',
            open && 'rotate-180',
          )}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-5 px-5 pb-5">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** A small selectable option in a row of choices. */
function PillButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-lg border px-2 py-2 text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500',
        disabled && 'cursor-not-allowed opacity-40',
        active
          ? 'border-violet-500 bg-violet-500/20 text-white'
          : 'border-white/15 bg-transparent text-slate-300 hover:bg-white/5',
      )}
    >
      {children}
    </button>
  );
}

/**
 * A compact segmented control that sits at the end of a checkbox row, for
 * choices that only qualify *how* that one field is drawn. Kept to icon-sized
 * labels so the row still reads as a single line.
 */
function InlineChoice<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  /** Names the group for screen readers; the row's own text is the visual label. */
  label: string;
  value: T;
  onChange: (value: T) => void;
  options: { id: T; label: string; title: string }[];
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex shrink-0 overflow-hidden rounded-lg border border-white/15"
    >
      {options.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            title={opt.title}
            aria-label={opt.title}
            aria-pressed={active}
            onClick={() => onChange(opt.id)}
            className={cn(
              'px-2 py-1 text-xs transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-violet-500',
              active
                ? 'bg-violet-500/25 text-white'
                : 'bg-transparent text-slate-400 hover:bg-white/5 hover:text-slate-200',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function OrientationToggle({
  value,
  active,
  children,
}: {
  value: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <ToggleGroupItem
      value={value}
      className={cn(
        'flex h-auto items-center gap-2 rounded-lg border py-2.5 text-sm transition-colors',
        active
          ? 'border-violet-500 bg-violet-500/20 text-white data-[state=on]:bg-violet-500/20 data-[state=on]:text-white'
          : 'border-white/15 bg-transparent text-slate-300 hover:bg-white/5',
      )}
    >
      {children}
    </ToggleGroupItem>
  );
}

function ThemeBadge({
  hasTheme,
  themeTitle,
  isLoading,
}: {
  hasTheme: boolean;
  themeTitle?: string;
  isLoading: boolean;
}) {
  if (isLoading) return null;
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-slate-400">
      <Palette className="h-3.5 w-3.5 text-violet-400" />
      {hasTheme
        ? <>Using theme{themeTitle ? <>: <span className="text-slate-200">{themeTitle}</span></> : ''}</>
        : 'No active theme — using default styling'}
    </div>
  );
}

function CardSkeleton({ orientation }: { orientation: Orientation }) {
  const { width, height } = cardSize(orientation);
  return (
    <Skeleton
      className="w-full rounded-2xl bg-white/10"
      style={{ maxWidth: width, aspectRatio: `${width} / ${height}` }}
    />
  );
}
